/**
 * Actions over a selection.
 *
 * Sits between `Panel` and `DataTable` rather than inside the table: `DataTable`'s outer
 * element is a single scrolling div, so a bar inside it scrolls away with the rows, and
 * `Panel` has no footer slot to put it in.
 */
import { useState } from "react";
import { usePermission } from "../hooks/useSession";
import { Button } from "./ui";

export function BulkBar({
  count,
  actions,
  onClear,
  failures,
}: {
  count: number;
  actions: readonly { label: string; permission: string; run: () => Promise<void> }[];
  onClear: () => void;
  /** Rendered one per line, `name — reason`; never collapsed into "some failed". */
  failures?: { id: string; label: string; error: string }[];
}) {
  const [running, setRunning] = useState<string | null>(null);
  // usePermission() itself takes no argument and must be called unconditionally, at a
  // fixed position, exactly once per render — calling a hook inside `.filter()`'s
  // callback would vary the hook count with `actions.length` and break the rules of
  // hooks. Call it here, once, and filter against the predicate it returns.
  const can = usePermission();
  const allowed = actions.filter((action) => can(action.permission));

  if (count === 0) return null;

  return (
    <div className="shrink-0 border-b border-hair bg-gold-wash">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <span className="font-mono text-[11px] font-bold tracking-[0.14em] text-gold uppercase">
          {count} selected
        </span>
        {allowed.map((action) => (
          <Button
            key={action.label}
            disabled={running !== null}
            onClick={async () => {
              setRunning(action.label);
              try {
                await action.run();
              } finally {
                setRunning(null);
              }
            }}
          >
            {action.label}
          </Button>
        ))}
        <Button variant="ghost" onClick={onClear} className="ms-auto">
          clear selection
        </Button>
      </div>
      {failures && failures.length > 0 && (
        <ul className="border-t border-hair px-3 py-2">
          {failures.map((failure) => (
            <li key={failure.id} className="font-mono text-[11px] text-crit">
              {failure.label} — {failure.error}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
